import {
  booleanAttribute,
  Component,
  forwardRef,
  input,
  InputSignal,
  InputSignalWithTransform,
  signal,
  WritableSignal
} from '@angular/core';

import { BLOCKABLE } from '../../domain/blockable.interface';
import { SpinnerComponent } from '../loading-indicator/spinner.component';
import type { Blockable } from '../../domain/blockable.interface';
import type {
  Type
} from '@angular/core';

export type IconButtonShape = 'square' | 'circle' | 'chip';
export type IconButtonSize = 'extra-large' | 'large' | 'regular' | 'small' | 'extra-small' | 'tiny';
export type IconButtonBackground = 'clear' | 'clear-tint' |
  'white' | 'tint' | 'secondary-tint' | 'grey' | 'elevation' |
  'dark-bg' | 'clear-grey' | 'all-grey' | 'black';
export type IconButtonLabelSize = 'small' | 'small-bold' | 'large' | 'large-bold';

@Component({
  imports: [
    SpinnerComponent
  ],
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: '[tdWbIconButton]',
  exportAs: 'tdWbIconButton',
  templateUrl: 'icon-button.component.html',
  styleUrls: ['icon-button.component.scss'],
  providers: [{
    provide: BLOCKABLE,
    useExisting: forwardRef((): Type<IconButtonComponent> => IconButtonComponent)
  }],
  host: {
    '[attr.type]': '"button"',
    '[attr.disabled]': 'disabled() ? "" : null',
    '[class.td-wb-icon-button--square]': 'buttonShape() === "square"',
    '[class.td-wb-icon-button--chip]': 'buttonShape() === "chip"',
    '[class.td-wb-icon-button--extra-large]': 'buttonSize() === "extra-large"',
    '[class.td-wb-icon-button--large]': 'buttonSize() === "large"',
    '[class.td-wb-icon-button--small]': 'buttonSize() === "small"',
    '[class.td-wb-icon-button--extra-small]': 'buttonSize() === "extra-small"',
    '[class.td-wb-icon-button--tiny]': 'buttonSize() === "tiny"',
    '[class.td-wb-icon-button--clear-tint]': 'buttonBackground() === "clear-tint"',
    '[class.td-wb-icon-button--clear-grey]': 'buttonBackground() === "clear-grey"',
    '[class.td-wb-icon-button--white]': 'buttonBackground() === "white"',
    '[class.td-wb-icon-button--tint]': 'buttonBackground() === "tint"',
    '[class.td-wb-icon-button--secondary-tint]': 'buttonBackground() === "secondary-tint"',
    '[class.td-wb-icon-button--grey]': 'buttonBackground() === "grey"',
    '[class.td-wb-icon-button--all-grey]': 'buttonBackground() === "all-grey"',
    '[class.td-wb-icon-button--elevation]': 'buttonBackground() === "elevation"',
    '[class.td-wb-icon-button--dark]': 'buttonBackground() === "dark-bg"',
    '[class.td-wb-icon-button--black]': 'buttonBackground() === "black"',
    '[class.td-wb-icon-button--spinner]': 'showSpinner()',
    '(mouseenter)': 'onMouseEnter()',
    '(focusin)': 'onMouseEnter()',
    '(mouseleave)': 'onMouseLeave()',
    '(focusout)': 'onMouseLeave()'
  }
})
export class IconButtonComponent implements Blockable{
  buttonShape: InputSignal<IconButtonShape> = input<IconButtonShape>('circle', { alias: 'tdWbIconButtonShape' });
  buttonSize: InputSignal<IconButtonSize> = input<IconButtonSize>('regular', { alias: 'tdWbIconButtonSize' });
  buttonBackground: InputSignal<IconButtonBackground> = input<IconButtonBackground>('clear', { alias: 'tdWbIconButtonBackground' });
  iconButtonLabelText: InputSignal<string | undefined> = input<string | undefined>(undefined, { alias: 'tdWbIconButtonLabelText' });
  iconButtonLabelPosition: InputSignal<'top' | 'bottom'> = input<'top' | 'bottom'>('bottom', { alias: 'tdWbIconButtonLabelPosition' });
  iconButtonLabelSize: InputSignal<IconButtonLabelSize> = input<IconButtonLabelSize>('small', { alias: 'tdWbIconButtonLabelSize' });

  disabled: InputSignalWithTransform<boolean, unknown> = input(false, { transform: booleanAttribute });

  active: WritableSignal<boolean> = signal<boolean>(false);
  showSpinner: WritableSignal<boolean> = signal<boolean>(false);

  onMouseEnter(): void {
    this.active.set(true);
  }

  onMouseLeave(): void {
    this.active.set(false);
  }

  /**
   * See Blockable.setBlocked. Called from tdWbBlock
   *
   * @param value
   */
  setBlocked(value: boolean): void {
    this.showSpinner.set(value);
  }

  isBlocked(): boolean {
    return this.showSpinner();
  }
}
